/** Chat timestamps are unix ms numbers on the wire but legacy persisted entries may
 * still be ISO strings. These helpers normalize the value before display so the user
 * always sees their local time, never UTC or server time. */

export type ChatTimestamp = number | string | undefined | null;

export function toUnixMs(value: ChatTimestamp): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.length > 0) {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function toDate(value: ChatTimestamp): Date {
  return new Date(toUnixMs(value));
}

/** "9:41 AM" — short local clock time. */
export function formatClockTime(value: ChatTimestamp): string {
  return toDate(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "Today" / "Yesterday" / "Tuesday" / "May 14" — local-time aware separator label. */
export function formatChatDateSeparator(value: ChatTimestamp): string {
  const date = toDate(value);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return date.toLocaleDateString(undefined, { weekday: "long" });
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

/** Short relative: "now", "5m", "2h", "3d", or "May 14". */
export function formatRelativeShort(value: ChatTimestamp): string {
  const ms = toUnixMs(value);
  if (!ms) return "";
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function nowMs(): number {
  return Date.now();
}
